import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCreatePartnerMutation } from "@/features/api/partnersApi";
import { Button } from "@/components/ui/button";

const CreatePartner = () => {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState(""); // <-- Added state for errors

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    orgName: "",
    orgWebsite: "",
    partnershipTypes: [],
    orgDescription: "",
    whyPartner: "",
    preferredContact: "",
    candidatesSent: "",
    comments: "",
    consent: false,
  });

  const [createPartner, { isLoading }] = useCreatePartnerMutation();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox" && name === "partnershipTypes") {
      setFormData((prev) => {
        const alreadySelected = prev.partnershipTypes.includes(value);
        return {
          ...prev,
          partnershipTypes: alreadySelected
            ? prev.partnershipTypes.filter((item) => item !== value)
            : [...prev.partnershipTypes, value],
        };
      });
    } else if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(""); // reset error on new submit

    if (!formData.consent) {
      alert("Please give your consent to be contacted.");
      return;
    }

    try {
      const formDataToSend = {
        ...formData,
        candidatesSent: Number(formData.candidatesSent) || 0,
      };

      await createPartner(formDataToSend).unwrap();

      alert("Partner application submitted successfully!");
      navigate("/admin/partner-list");
    } catch (err) {
      // Capture specific error message from API
      const message =
        err?.data?.message || "Failed to submit application. Please try again.";
      setErrorMessage(message);
      console.error("Create Partner error:", err);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-4">Add Partners Form</h1>

      {errorMessage && (
        <div className="bg-red-100 text-red-800 p-3 mb-4 rounded">
          {errorMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Full Name */}
        <div>
          <label htmlFor="fullName" className="block font-medium mb-1">
            1. Full Name:
          </label>
          <input
            id="fullName"
            name="fullName"
            type="text"
            placeholder="Enter your full name"
            value={formData.fullName}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 p-2 rounded"
          />
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block font-medium mb-1">
            2. Email Address:
          </label>
          <input
            id="email"
            name="email"
            type="email"
            placeholder="Enter your email address"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 p-2 rounded"
          />
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="phone" className="block font-medium mb-1">
            3. Phone Number:
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            placeholder="Enter your phone number"
            value={formData.phone}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 p-2 rounded"
          />
        </div>

        {/* Organization Name */}
        <div>
          <label htmlFor="orgName" className="block font-medium mb-1">
            4. Organization Name:
          </label>
          <input
            id="orgName"
            name="orgName"
            type="text"
            placeholder="Enter the name of your organization"
            value={formData.orgName}
            onChange={handleChange}
            className="w-full border border-gray-300 p-2 rounded"
          />
        </div>

        {/* Organization Website */}
        <div>
          <label htmlFor="orgWebsite" className="block font-medium mb-1">
            5. Organization Website:
          </label>
          <input
            id="orgWebsite"
            name="orgWebsite"
            type="url"
            placeholder="Enter your organization's website URL"
            value={formData.orgWebsite}
            onChange={handleChange}
            className="w-full border border-gray-300 p-2 rounded"
          />
        </div>

        {/* Partnership Types */}
        <div>
          <label className="block font-medium mb-1">
            6. Type of Partnership Interested In:
          </label>
          <div className="flex flex-col gap-2 pl-4">
            {[
              "Study Abroad Consultancy",
              "Work Permit Services",
              "Visitor Visa Services",
              "English Language Teaching",
              "Other",
            ].map((type) => (
              <label key={type} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="partnershipTypes"
                  value={type}
                  checked={formData.partnershipTypes.includes(type)}
                  onChange={handleChange}
                  className="h-4 w-4"
                />
                {type}
              </label>
            ))}
          </div>
        </div>

        {/* Organization Description */}
        <div>
          <label htmlFor="orgDescription" className="block font-medium mb-1">
            7. Describe Your Organization:
          </label>
          <textarea
            id="orgDescription"
            name="orgDescription"
            placeholder="Provide a brief description of your organization and its services"
            value={formData.orgDescription}
            onChange={handleChange}
            rows={4}
            className="w-full border border-gray-300 p-2 rounded"
          />
        </div>

        {/* Why Partner */}
        <div>
          <label htmlFor="whyPartner" className="block font-medium mb-1">
            8. Why Do You Want to Partner With Us?
          </label>
          <textarea
            id="whyPartner"
            name="whyPartner"
            placeholder="Share your reasons for wanting to partner with us"
            value={formData.whyPartner}
            onChange={handleChange}
            rows={4}
            className="w-full border border-gray-300 p-2 rounded"
          />
        </div>

        {/* Preferred Contact */}
        <div>
          <label className="block font-medium mb-1">
            9. Preferred Communication Method:
          </label>
          <div className="flex gap-6 pl-4">
            {["Email", "Phone", "Video Call"].map((method) => (
              <label key={method} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="preferredContact"
                  value={method}
                  checked={formData.preferredContact === method}
                  onChange={handleChange}
                  className="h-4 w-4"
                />
                {method}
              </label>
            ))}
          </div>
        </div>

        {/* Candidates Sent */}
        <div>
          <label htmlFor="candidatesSent" className="block font-medium mb-1">
            10. Number of Candidates Sent Abroad in the Past Year:
          </label>
          <input
            id="candidatesSent"
            name="candidatesSent"
            type="number"
            min="0"
            placeholder="Enter the total number of candidates sent abroad in the last 12 months"
            value={formData.candidatesSent}
            onChange={handleChange}
            className="w-full border border-gray-300 p-2 rounded"
          />
        </div>

        {/* Additional Comments */}
        <div>
          <label htmlFor="comments" className="block font-medium mb-1">
            11. Additional Comments or Questions:
          </label>
          <textarea
            id="comments"
            name="comments"
            placeholder="Any other information you would like to provide"
            value={formData.comments}
            onChange={handleChange}
            rows={3}
            className="w-full border border-gray-300 p-2 rounded"
          />
        </div>

        {/* Consent */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            name="consent"
            id="consent"
            checked={formData.consent}
            onChange={handleChange}
            className="h-4 w-4"
          />
          <label htmlFor="consent" className="select-none">
            12. I consent to being contacted regarding partnership opportunities.
          </label>
        </div>

        {/* Buttons */}
        <div className="flex items-center gap-4 pt-4">
          <Button
            type="button"
            onClick={() => navigate("/admin/partner-list")}
            disabled={isLoading}
            className="bg-white border border-gray-400 text-gray-700 px-4 py-2 rounded hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            Back to Pages
          </Button>

          <Button
            type="submit"
            disabled={isLoading}
            className="bg-blue-600 text-white px-6 py-3 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading ? (
              <svg
                className="mr-2 h-5 w-5 animate-spin inline-block"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            ) : (
              "Submit Application"
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreatePartner;
